import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Bell, ForkKnife, MagnifyingGlass } from "@phosphor-icons/react";

const NEED_OPTIONS = [
  { id: "assistance", label: "Assistance" },
  { id: "order", label: "Ready to order" },
  { id: "bill", label: "Request bill" },
  { id: "water", label: "Water / drinks" },
  { id: "other", label: "Other" },
];

export default function TableGuest() {
  const { token } = useParams();
  const [table, setTable] = useState(null);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [q, setQ] = useState("");
  const [need, setNeed] = useState("assistance");
  const [message, setMessage] = useState("");
  const [calling, setCalling] = useState(false);

  const load = async () => {
    try {
      const [{ data: t }, { data: o }] = await Promise.all([
        api.get(`/public/table/${token}`),
        api.get(`/public/table/${token}/order`),
      ]);
      setTable(t);
      setOrder(o.order);
      setError(null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Invalid table QR");
      setTable(null);
    }
  };

  useEffect(() => {
    load();
    api.get("/menu", { params: { available_only: true } }).then((r) => setItems(r.data));
    api.get("/categories").then((r) => setCats(r.data));
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filtered = useMemo(() => items.filter((it) => {
    if (activeCat !== "all" && it.category_id !== activeCat) return false;
    if (q && !it.name.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, activeCat, q]);

  const callWaiter = async () => {
    setCalling(true);
    try {
      await api.post(`/public/table/${token}/call-waiter`, { need, message: message || null });
      toast.success("Staff notified — someone will be with you shortly");
      setMessage("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not call waiter");
    } finally {
      setCalling(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" data-testid="table-qr-invalid">
        <div className="max-w-md text-center">
          <div className="label-eyebrow mb-3">Table QR</div>
          <h1 className="font-display font-extrabold text-2xl mb-2">This code is not valid</h1>
          <p className="text-sm text-[var(--text-secondary)] mb-6">{error}</p>
          <Button asChild variant="outline"><Link to="/menu">Browse public menu</Link></Button>
        </div>
      </div>
    );
  }

  if (!table) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-[var(--text-secondary)]">
        Loading table…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]" data-testid="table-guest-page">
      <header className="glass sticky top-0 z-30 px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[var(--brand)] text-white grid place-items-center font-display font-black text-xl">R</div>
            <div>
              <div className="font-display font-extrabold text-lg leading-none">Table {table.number}</div>
              <div className="label-eyebrow text-[9px] mt-1">{table.section} · {table.status}</div>
            </div>
          </div>
          <ForkKnife size={22} weight="duotone" className="text-[var(--brand)]" />
        </div>
        <p className="text-xs text-[var(--text-secondary)] mt-3 leading-relaxed">
          Welcome. Browse the menu below. <strong>Orders for this table are placed by our staff only</strong> —
          use Call waiter when you are ready.
        </p>
      </header>

      <div className="px-4 sm:px-6 py-4 space-y-6 max-w-3xl mx-auto">
        {/* Call waiter */}
        <section className="bento p-4 space-y-3" data-testid="call-waiter-panel">
          <div className="flex items-center gap-2 font-semibold text-sm">
            <Bell size={18} weight="duotone" /> Call waiter
          </div>
          <div className="flex flex-wrap gap-2">
            {NEED_OPTIONS.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => setNeed(n.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                  need === n.id ? "bg-[var(--brand)] text-white" : "bg-[var(--bg-secondary)]"
                }`}
              >
                {n.label}
              </button>
            ))}
          </div>
          <Input
            placeholder="Optional note…"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            data-testid="waiter-message"
          />
          <Button className="btn-primary w-full" disabled={calling} onClick={callWaiter} data-testid="call-waiter-btn">
            {calling ? "Notifying…" : "Notify restaurant staff"}
          </Button>
        </section>

        {/* Order status */}
        <section className="bento p-4" data-testid="table-guest-order">
          <div className="label-eyebrow mb-2">Your order status</div>
          {!order ? (
            <p className="text-sm text-[var(--text-secondary)]">No active order yet. Ask staff to take your order.</p>
          ) : (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="font-semibold">{order.order_number}</span>
                <span className="uppercase text-xs tracking-wider font-bold text-[var(--brand)]">{order.status}</span>
              </div>
              <ul className="text-sm space-y-1">
                {order.items?.map((it, i) => (
                  <li key={i}>{it.quantity}× {it.name}</li>
                ))}
              </ul>
              {order.total != null && (
                <div className="text-sm font-semibold pt-2 border-t border-[#EAE7E0]">
                  Total {fmtMoney(order.total)} · {order.payment_status}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Menu browse */}
        <section>
          <div className="label-eyebrow mb-3">Menu</div>
          <div className="relative mb-3">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" size={16} />
            <Input className="pl-9" placeholder="Search dishes…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
            <button
              type="button"
              onClick={() => setActiveCat("all")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap ${activeCat === "all" ? "bg-[var(--brand)] text-white" : "bg-white border border-[#EAE7E0]"}`}
            >
              All
            </button>
            {cats.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveCat(c.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap ${activeCat === c.id ? "bg-[var(--brand)] text-white" : "bg-white border border-[#EAE7E0]"}`}
              >
                {c.name}
              </button>
            ))}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {filtered.map((it) => (
              <div key={it.id} className="bento p-3 flex gap-3">
                {it.image_url && (
                  <img src={it.image_url} alt="" className="w-16 h-16 rounded-md object-cover" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{it.name}</div>
                  <div className="text-xs text-[var(--text-secondary)] line-clamp-2">{it.description}</div>
                  <div className="text-sm font-bold mt-1">{fmtMoney(it.price)}</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
